import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Building2, Network } from "lucide-react";
import { branchesService } from "@/services/branchesService";
import {
  aggregateBranchStatus,
  fetchBranchEntityStatuses,
  isHistoricalSyncActive,
  isHistoricalSyncComplete,
} from "@/services/crmGatewaySyncService";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { cn } from "@/lib/cn";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { InfoHint } from "@/components/atoms/InfoHint";
import { MenuSelect, type MenuSelectOption } from "@/components/molecules/MenuSelect";
import Dialog from "@/components/atoms/Dialog";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { flattenEntities, findEntityById } from "@/modules/settings/entityHelpers";
import {
  InfoCallout,
  OnOffLabel,
  SaveStatus,
  TooltipWithLink,
} from "@/modules/settings/SettingsPrimitives";
import { SCOPE_BAR_CLASS, actionBarClass } from "@/modules/settings/settingsStyles";
import { Badge } from "@/components/atoms/Badge";
import type { BranchHierarchy, LegalEntityHierarchy } from "@/services/profileService";

const BRANCH_RESYNC_COOLDOWN_STORAGE_KEY = "crm-branch-resync-cooldown-until";

/**
 * Lead categories the automation can be switched on/off for. Keys are what the
 * backend stores in `leadsProcessingConfig`; the examples name the raw lead
 * intents that fall into each bucket.
 */
const LEAD_CATEGORIES: { key: string; label: string }[] = [
  { key: "LETTINGS", label: "Lettings (e.g. LookingToRent, RENT)" },
  { key: "SALES", label: "Sales (e.g. LookingToBuy, Buy)" },
  { key: "LETTINGS_VALUATION", label: "Lettings valuation (e.g. LookingToLet)" },
  { key: "SALES_VALUATION", label: "Sales valuation (e.g. LookingToSell)" },
  { key: "GENERAL", label: "General (e.g. Unspecified, Other)" },
];

type LeadsConfig = Record<string, boolean>;

/** Missing or false means disabled — only explicit true is treated as enabled. */
function isCategoryEnabled(config: LeadsConfig, key: string): boolean {
  return config[key] === true;
}

function serializeLeadsConfigForSave(config: LeadsConfig): LeadsConfig {
  const serialized: LeadsConfig = {};
  for (const { key } of LEAD_CATEGORIES) {
    serialized[key] = isCategoryEnabled(config, key);
  }
  return serialized;
}

function leadsConfigEquals(a: LeadsConfig, b: LeadsConfig): boolean {
  const normA = { ...serializeLeadsConfigForSave(a) };
  const normB = { ...serializeLeadsConfigForSave(b) };
  for (const key of Object.keys(a)) {
    if (!(key in normA)) normA[key] = a[key];
  }
  for (const key of Object.keys(b)) {
    if (!(key in normB)) normB[key] = b[key];
  }

  const keysA = Object.keys(normA);
  const keysB = Object.keys(normB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => normA[key] === normB[key]);
}

function readBranchResyncCooldownUntil(now = Date.now()): Record<string, number> {
  try {
    const raw = localStorage.getItem(BRANCH_RESYNC_COOLDOWN_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    const active: Record<string, number> = {};
    for (const [id, until] of Object.entries(parsed)) {
      if (until > now) active[id] = until;
    }
    return active;
  } catch {
    return {};
  }
}

function isBranchResyncInProgress(
  branchId: string | null | undefined,
  cooldownUntil: Record<string, number>,
  now = Date.now(),
): boolean {
  if (!branchId) return false;
  return now < (cooldownUntil[branchId] ?? 0);
}

/**
 * The integration page uses a fixed cooldown after queueing a branch sync.
 * A completed historical-sync response is fresher backend evidence and must
 * take precedence over that client-side timer.
 */
export function isBranchConfigurationSyncActive(
  branchId: string | null | undefined,
  cooldownUntil: Record<string, number> = {},
  historicalStatus: string | null | undefined,
  now = Date.now(),
): boolean {
  return (
    !isHistoricalSyncComplete(historicalStatus) &&
    isBranchResyncInProgress(branchId, cooldownUntil, now)
  );
}

function findBranchInHierarchy(
  entities: LegalEntityHierarchy[],
  branchId: string,
): BranchHierarchy | undefined {
  for (const le of entities) {
    const direct = le.branches?.find((branch) => branch.id === branchId);
    if (direct) return direct;
    const nested = findBranchInHierarchy(le.subEntities ?? [], branchId);
    if (nested) return nested;
  }
  return undefined;
}

function findBranchOwner(
  entities: LegalEntityHierarchy[],
  branchId: string,
): LegalEntityHierarchy | undefined {
  for (const entity of entities) {
    if (entity.branches?.some((b) => b.id === branchId)) return entity;
    const found = findBranchOwner(entity.subEntities ?? [], branchId);
    if (found) return found;
  }
  return undefined;
}

function findActiveCrmInHierarchy(entities: LegalEntityHierarchy[]): string | null {
  for (const le of flattenEntities(entities)) {
    if (le.sourceCrm?.trim()) return le.sourceCrm.trim();
    for (const branch of le.branches ?? []) {
      if (branch.sourceCrm?.trim()) return branch.sourceCrm.trim();
    }
  }
  return null;
}

function normalizeCrmId(crm: string): string {
  const upper = crm.trim().toUpperCase();
  if (upper === "PROPCO") return "PROPCO";
  if (upper === "ALTO") return "ALTO";
  if (upper === "REAPIT") return "reapit";
  if (upper === "APEX27") return "APEX27";
  return crm.trim();
}

interface Props {
  legalEntities: LegalEntityHierarchy[];
  /** Scope the form opens on — seeded from the app context, then owned locally. */
  initialLegalEntityId?: string | null;
  initialBranchId?: string | null;
}

/**
 * Leads processing settings. Stored per branch on the branch record, so unlike
 * viewing settings there is no scope ladder — a concrete branch has to be
 * picked before anything can be read or written. The selects belong to this
 * form only and never touch the global app context shown in the header.
 */
export function LeadsProcessingSection({
  legalEntities,
  initialLegalEntityId,
  initialBranchId,
}: Props) {
  const allLegalEntities = useMemo(() => flattenEntities(legalEntities), [legalEntities]);

  // Form-local scope. Seeded once from the app context; never written back.
  const [legalEntityId, setLegalEntityId] = useState<string | null>(initialLegalEntityId ?? null);
  const [branchId, setBranchId] = useState<string | null>(initialBranchId ?? null);

  // Fall back to the first available legal entity when the seeded one isn't
  // part of the profile hierarchy (or nothing was seeded).
  useEffect(() => {
    if (allLegalEntities.length === 0) return;
    if (legalEntityId && allLegalEntities.some((le) => le.id === legalEntityId)) return;
    setLegalEntityId(allLegalEntities[0].id);
    setBranchId(null);
  }, [allLegalEntities, legalEntityId]);

  const [leadsConfig, setLeadsConfig] = useState<LeadsConfig>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedLeadsConfig, setSavedLeadsConfig] = useState<LeadsConfig>({});
  const [defaultLeadSource, setDefaultLeadSource] = useState<string | null>(null);
  const [branchDetailSourceCrm, setBranchDetailSourceCrm] = useState<string | null>(null);
  const [historicalSyncStatus, setHistoricalSyncStatus] = useState<string | null>(null);
  const [branchResyncCooldownUntil, setBranchResyncCooldownUntil] = useState(
    readBranchResyncCooldownUntil,
  );

  const selectedBranch = useMemo(
    () => (branchId ? findBranchInHierarchy(legalEntities, branchId) : undefined),
    [legalEntities, branchId],
  );

  const selectedLe = useMemo(
    () => findEntityById(legalEntities, legalEntityId ?? ""),
    [legalEntities, legalEntityId],
  );

  const owningLe = useMemo(
    () => (branchId ? findBranchOwner(legalEntities, branchId) : undefined),
    [legalEntities, branchId],
  );

  const resolvedCrm = useMemo(() => {
    const raw =
      selectedBranch?.sourceCrm?.trim() ||
      branchDetailSourceCrm ||
      owningLe?.sourceCrm?.trim() ||
      selectedLe?.sourceCrm?.trim() ||
      findActiveCrmInHierarchy(legalEntities);
    return raw ? normalizeCrmId(raw) : null;
  }, [selectedBranch, branchDetailSourceCrm, owningLe, selectedLe, legalEntities]);

  const historicalSyncComplete = isHistoricalSyncComplete(historicalSyncStatus);
  const branchResyncInProgress = isBranchConfigurationSyncActive(
    branchId,
    branchResyncCooldownUntil,
    historicalSyncStatus,
  );
  const historicalSyncRunning = isHistoricalSyncActive(historicalSyncStatus);
  const syncInProgress = historicalSyncRunning || branchResyncInProgress;
  const branchAwaitingInitialSync = selectedBranch?.branchSynced === false;

  // Where the branch's CRM sync is run:
  // - Propco is a group-wide account with no per-branch rows, so route to /integrations/PROPCO
  // - Ref-mapped CRMs (Alto, Reapit, etc.) support ?branch= to glide to the row and flash the action/ref control
  // - Falls back to /integrations if no CRM is configured
  const crmSyncHref = useMemo(() => {
    if (!resolvedCrm) return "/integrations";
    if (resolvedCrm.toUpperCase() === "PROPCO") return "/integrations/PROPCO";
    if (resolvedCrm.toUpperCase() === "REAPIT") return "/integrations/REAPIT";
    return branchId
      ? `/integrations/${encodeURIComponent(resolvedCrm)}?branch=${encodeURIComponent(branchId)}`
      : `/integrations/${encodeURIComponent(resolvedCrm)}`;
  }, [resolvedCrm, branchId]);

  // Load the selected branch's saved config. A branch with nothing saved reads
  // as an empty object, which the defaulting below turns into "all disabled".
  useEffect(() => {
    if (!branchId) {
      setLeadsConfig({});
      setSavedLeadsConfig({});
      setDefaultLeadSource(null);
      setBranchDetailSourceCrm(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void branchesService
      .getOne(branchId)
      .then((b) => {
        if (cancelled) return;
        const initial = b.leadsProcessingConfig ?? {};
        setLeadsConfig(initial);
        setSavedLeadsConfig(initial);
        const source = b.defaultLeadSource?.trim();
        setDefaultLeadSource(source ? source : null);
        const crm = b.sourceCrm?.trim();
        setBranchDetailSourceCrm(crm ? crm : null);
      })
      .catch(() => {
        if (cancelled) return;
        setLeadsConfig({});
        setSavedLeadsConfig({});
        setDefaultLeadSource(null);
        setBranchDetailSourceCrm(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  // Historical sync must finish before lead processing can be turned on.
  useEffect(() => {
    if (!branchId) {
      setHistoricalSyncStatus(null);
      return;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    const refreshSyncStatus = async () => {
      try {
        const byBranch = await fetchBranchEntityStatuses([branchId]);
        if (cancelled) return;
        const next = aggregateBranchStatus(byBranch[branchId] ?? {});
        setHistoricalSyncStatus(next);
        return next;
      } catch {
        if (!cancelled) setHistoricalSyncStatus(null);
        return null;
      }
    };

    void refreshSyncStatus().then((status) => {
      if (cancelled || !isHistoricalSyncActive(status)) return;
      pollTimer = setInterval(() => {
        void refreshSyncStatus().then((next) => {
          if (cancelled || (!isHistoricalSyncActive(next) && pollTimer)) {
            clearInterval(pollTimer);
            pollTimer = undefined;
          }
        });
      }, 10_000);
    });

    return () => {
      cancelled = true;
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    };
  }, [branchId]);

  // Branch configuration resync on Integrations shares this localStorage cooldown.
  useEffect(() => {
    if (!branchId) return;

    const refreshCooldown = () => setBranchResyncCooldownUntil(readBranchResyncCooldownUntil());
    refreshCooldown();
    const pollTimer = setInterval(refreshCooldown, 2_000);
    return () => clearInterval(pollTimer);
  }, [branchId]);

  const isDirty = useMemo(
    () => !leadsConfigEquals(leadsConfig, savedLeadsConfig),
    [leadsConfig, savedLeadsConfig],
  );

  const unsavedGuard = useUnsavedChangesGuard(isDirty);

  function resetLeadsConfig() {
    setLeadsConfig(savedLeadsConfig);
  }

  async function saveLeadsConfig() {
    if (!branchId || syncInProgress) return;
    try {
      setSaving(true);
      const serialized = serializeLeadsConfigForSave(leadsConfig);

      await branchesService.update(branchId, { leadsProcessingConfig: serialized });

      setLeadsConfig(serialized);
      setSavedLeadsConfig(serialized);

      toast.success("Leads processing settings saved.");
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to save leads processing settings.");
    } finally {
      setSaving(false);
    }
  }

  const branches = selectedLe?.branches ?? [];

  const legalEntityOptions: MenuSelectOption[] = allLegalEntities.map((le) => ({
    value: le.id,
    label: le.name,
    subtitle: le.groupId,
  }));

  const branchOptions: MenuSelectOption[] = [
    { value: "", label: "All Branches" },
    ...branches.map((br) => ({
      value: br.id,
      label: br.name,
      subtitle: br.groupId,
    })),
  ];

  const enabledCount = LEAD_CATEGORIES.filter((cat) =>
    isCategoryEnabled(leadsConfig, cat.key),
  ).length;

  const saveReasons: string[] = [];
  if (!branchId) saveReasons.push("Select a branch");
  if (syncInProgress) saveReasons.push("Sync is still running");
  if (!isDirty) saveReasons.push("No changes to save");
  const saveDisabled = saveReasons.length > 0;

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-bridge-accent border-t-transparent" />
        </div>
      )}

      {/* Scope selection — applies to this form only, not the app-wide context.
          Pinned to the top of the page scroll, like Viewing Settings, so "what
          am I editing" stays on screen for the whole edit. */}
      <div className={SCOPE_BAR_CLASS}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <div className="mb-2 flex items-center gap-2 text-bridge-muted">
              <Building2 size={16} className="text-bridge-accent" />
              <h4 className="text-sm font-bold uppercase tracking-wider">Legal Entity</h4>
              <InfoHint content="The legal entity whose lead automation you want to edit. Changing it clears the selected branch." />
            </div>
            <MenuSelect
              hideLabel
              value={legalEntityId ?? ""}
              options={legalEntityOptions}
              placeholder="Select legal entity"
              searchable={legalEntityOptions.length > 6}
              searchPlaceholder="Search legal entities…"
              onChange={(val) =>
                unsavedGuard.confirm(() => {
                  setLegalEntityId(val);
                  setBranchId(null);
                })
              }
            />
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2 text-bridge-muted">
              <Network size={16} className="text-bridge-accent" />
              <h4 className="text-sm font-bold uppercase tracking-wider">Branch</h4>
              <InfoHint
                content={
                  legalEntityId
                    ? "Lead automation is configured per branch — pick one to edit its settings."
                    : "Select a legal entity first to list its branches."
                }
              />
            </div>
            <MenuSelect
              hideLabel
              value={branchId ?? ""}
              options={branchOptions}
              placeholder="All Branches"
              disabled={!legalEntityId}
              searchable={branches.length > 6}
              searchPlaceholder="Search branches…"
              onChange={(val) => unsavedGuard.confirm(() => setBranchId(val || null))}
            />
          </div>
        </div>
      </div>

      <div className="px-5 py-5">
        <div className="w-full lg:w-[70%]">
          {branchId ? (
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="text-xs text-bridge-muted">
                Enable or disable automated contact creation and reachout for incoming leads by
                category.
              </p>
              <Badge tone={enabledCount > 0 ? "accent" : "neutral"} className="shrink-0">
                {enabledCount} of {LEAD_CATEGORIES.length} on
              </Badge>
            </div>
          ) : (
            <div className="mb-4">
              <InfoCallout>Select a branch above to edit these settings.</InfoCallout>
            </div>
          )}

          {branchId && syncInProgress && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {branchResyncInProgress
                ? "Branch configuration sync is still running. Lead processing settings cannot be changed until it completes."
                : "Historical sync is still running for this branch. Lead processing settings cannot be changed until sync completes."}
            </div>
          )}

          {branchId && !syncInProgress && branchAwaitingInitialSync && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              This branch has not finished its initial CRM sync yet. Lead processing categories
              cannot be turned on until sync completes.
            </div>
          )}

          {branchId && (
            <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="font-medium text-bridge-fg">Default lead source</span>
              <InfoHint content="Portal or source assigned to this branch in CRM configuration. Shown for reference — edit it in your CRM or branch setup, not here." />
              <span className="text-bridge-muted">{defaultLeadSource ?? "—"}</span>
            </div>
          )}

          <div className="space-y-2">
            {LEAD_CATEGORIES.map((cat) => {
              const enabled = isCategoryEnabled(leadsConfig, cat.key);
              const enableBlocked =
                !historicalSyncComplete || branchAwaitingInitialSync || branchResyncInProgress;
              const toggleDisabled = !branchId || syncInProgress || (enableBlocked && !enabled);
              const toggleTooltip = !branchId
                ? "These settings are branch-specific. Select a branch above to change this."
                : syncInProgress
                  ? branchResyncInProgress
                    ? "Branch configuration sync is still running. Try again once it completes."
                    : "Historical sync is still running. Lead processing settings cannot be changed until sync completes."
                  : enableBlocked && !enabled
                    ? (
                        // The fix — running the CRM sync — lives on Integrations.
                        <TooltipWithLink to={crmSyncHref} label="Open CRM integration">
                          Lead processing can only be enabled after this branch&apos;s CRM sync has
                          completed.
                        </TooltipWithLink>
                      )
                    : "On means leads in this category get a contact created and an automated reachout. Off leaves them for manual handling.";

              return (
                <div
                  key={cat.key}
                  className={cn(
                    "flex items-center justify-between rounded-lg border px-3 py-3 transition-colors",
                    enabled
                      ? "border-bridge-accent/25 bg-bridge-accent/5"
                      : "border-bridge-border bg-white",
                  )}
                >
                  <span
                    className={cn(
                      "text-sm font-medium",
                      branchId ? "text-bridge-fg" : "text-bridge-muted",
                    )}
                  >
                    {cat.label}
                  </span>
                  <div className="flex items-center gap-2.5">
                  <OnOffLabel on={enabled} />
                  <Tooltip content={toggleTooltip} side="top">
                    {/* A disabled button never fires mouse events, so the hover
                        target has to be this wrapper, not the button itself. */}
                    <span className={cn("inline-flex", toggleDisabled && "cursor-not-allowed")}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        aria-label={cat.label}
                        disabled={toggleDisabled}
                        onClick={() =>
                          setLeadsConfig((current) => {
                            const next = { ...current };
                            if (enabled) {
                              delete next[cat.key];
                            } else {
                              next[cat.key] = true;
                            }
                            return next;
                          })
                        }
                        className={cn(
                          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-bridge-accent focus:ring-offset-2",
                          branchId && !toggleDisabled
                            ? "cursor-pointer"
                            : "pointer-events-none cursor-not-allowed opacity-50",
                          enabled ? "bg-bridge-accent" : "border border-bridge-border bg-bridge-surface",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            "pointer-events-none inline-block h-4 w-4 transform rounded-full shadow ring-0 transition duration-200 ease-in-out",
                            enabled ? "translate-x-4 bg-white" : "translate-x-0 bg-bridge-muted",
                          )}
                        />
                      </button>
                    </span>
                  </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer bar — the last child of the card, spanning it edge to edge, and
          docked to the bottom of the page scroll so Save is always in reach. */}
      <div className={actionBarClass(isDirty)}>
        <SaveStatus dirty={isDirty} />
        <span className="ml-auto" />
        <Button
          variant="outline"
          onClick={resetLeadsConfig}
          disabled={!isDirty || saving}
          title="Discard changes and restore the last saved values"
        >
          Reset
        </Button>
        <Tooltip
          content={
            saveReasons.length > 1
              ? saveReasons.map((r) => `• ${r}`).join("\n")
              : saveReasons.join("")
          }
          side="top"
        >
          <span className={saveDisabled ? "inline-flex cursor-not-allowed" : "inline-flex"}>
            <Button
              variant="primary"
              onClick={saveLeadsConfig}
              loading={saving}
              disabled={saveDisabled}
              className={saveDisabled ? "pointer-events-none" : undefined}
            >
              Save processing settings
            </Button>
          </span>
        </Tooltip>
      </div>

      <Dialog
        open={unsavedGuard.open}
        title="Discard unsaved changes?"
        subtitle="Your edits to these leads processing settings have not been saved yet."
        submitLabel="Yes, discard"
        cancelLabel="No, stay here"
        // Both the X and the overlay mean "don't do it", same as No.
        onClose={unsavedGuard.cancel}
        onSubmit={unsavedGuard.proceed}
      >
        <p className="text-sm text-bridge-muted">
          Leaving this scope will lose the changes you have made. Save them first if you want to
          keep them.
        </p>
      </Dialog>
    </div>
  );
}
